import { FunctionComponent, ReactNode, useRef } from 'react'

import styled, { css } from 'styled-components'

import { mobileCSS } from '@measures/responsive'
import { printCSS } from '@utils/style'
import translate from '@i18n'
import useExpandableContent from '@hooks/useExpandableContent'
import PrimaryCTAButton from '@components/Buttons/PrimaryCTAButton'
import SecondaryCTAButton from '@components/Buttons/SecondaryCTAButton'

import { AnswerType } from '../types'
import AnswerFooter from './AnswerFooter'

const MAX_HEIGHT_DESKTOP = 60
const MAX_HEIGHT_MOBILE = 86

interface ExpandableContentProps {
  answer: AnswerType
  isExpandable: boolean
  children: ReactNode
  hasSources: boolean
  onSourcesClick: () => void
  onExpandClick: () => void
}

const StyledExpandableContent = styled.div`
  display: flex;
  flex-direction: column;
`

const TextContainer = styled.div<{ isExpanded: boolean }>`
  ${({
    theme: {
      colors: { fillDefault },
      utils: { opacity },
    },
    isExpanded,
  }) => css`
    transition: height 0.5s ease-out;

    ${printCSS(css`
      overflow: visible;
      transition: unset;
      height: max-content;
    `)};

    ${isExpanded
      ? css`
          height: auto;
        `
      : css`
          position: relative;
          overflow: hidden;
          height: ${MAX_HEIGHT_DESKTOP}px;

          ${mobileCSS(css`
            height: ${MAX_HEIGHT_MOBILE}px;
          `)}

          &:before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: linear-gradient(
              180deg,
              ${opacity(fillDefault, '00')} 0%,
              ${fillDefault} 100%
            );
          }
        `}
  `}
`

const ButtonsWrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing16 },
    },
  }) => css`
    position: relative;
    padding-top: ${spacing16};
    display: flex;
    justify-content: center;
    gap: ${spacing16};
    width: 100%;

    ${printCSS(css`
      display: none;
    `)}
  `}
`

const StyledPrimaryCTAButton = styled(PrimaryCTAButton)`
  flex: 1;
`

const ExpandableContent: FunctionComponent<ExpandableContentProps> = ({
  answer,
  isExpandable,
  children,
  hasSources,
  onSourcesClick,
  onExpandClick,
}) => {
  const componentRef = useRef<HTMLDivElement | null>(null)

  const { isExpanded, setIsExpanded } = useExpandableContent({
    componentRef,
  })

  const handleExpandClick = () => {
    setIsExpanded(!isExpanded)
    onExpandClick()
  }

  return isExpandable ? (
    <StyledExpandableContent>
      <TextContainer ref={componentRef} isExpanded={!!isExpanded}>
        <>
          {children}
          {isExpanded && (
            <AnswerFooter
              answer={answer}
              hasSources={hasSources}
              onSourcesClick={onSourcesClick}
            />
          )}
        </>
      </TextContainer>
      {isExpanded !== null && !isExpanded && (
        <ButtonsWrapper>
          <StyledPrimaryCTAButton
            onClick={handleExpandClick}
            size="small"
            iconName={!isExpanded ? 'chevron-down' : 'chevron-up'}
          >
            {translate('aiSearch.answerMessage.fullAnswer')}
          </StyledPrimaryCTAButton>
          {hasSources && (
            <SecondaryCTAButton
              onClick={onSourcesClick}
              size="small"
              colorVariant="black"
              iconName="link"
            >
              {translate('aiSearch.answerMessage.sources')}
            </SecondaryCTAButton>
          )}
        </ButtonsWrapper>
      )}
    </StyledExpandableContent>
  ) : (
    <>
      {children}
      <AnswerFooter
        answer={answer}
        hasSources={hasSources}
        onSourcesClick={onSourcesClick}
      />
    </>
  )
}

export default ExpandableContent
